import React from 'react';
import { AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { safeLocalStorage } from '../utils/safeStorage';
import posthog from 'posthog-js';
import {
  attemptChunkLoadRecovery,
  hardResetClientCaches,
  isChunkLoadMessage,
} from '../lib/productAnalytics';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      recovering: false,
    };
  }

  static getDerivedStateFromError(error) {
    // React.lazy chunk failures surface here through Suspense, never through
    // the window 'error' / 'unhandledrejection' listeners. Try the one-shot
    // soft reload before showing the error UI; if this tab already tried,
    // fall through and let the user pick "Clear cache & reload".
    const message = error?.message || String(error || '');
    if (isChunkLoadMessage(message) && attemptChunkLoadRecovery(message)) {
      return { hasError: true, error, recovering: true };
    }
    return { hasError: true, error, recovering: false };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary caught error]:', error, errorInfo);

    try {
      if (posthog && typeof posthog.capture === 'function') {
        posthog.capture('$exception', {
          $exception_message: error?.message || String(error),
          $exception_type: error?.name || 'ErrorBoundaryCrash',
          $exception_component_stack: errorInfo?.componentStack || '',
          source: 'ErrorBoundary',
        });
      }
    } catch {
      // ignore telemetry errors
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearCacheAndReload = async () => {
    safeLocalStorage.evictCaches();
    try {
      window.localStorage.removeItem('testara_testRuns');
      window.localStorage.removeItem('testara_testCases');
      window.localStorage.removeItem('testara_testPlans');
      window.localStorage.removeItem('testara_projects');
    } catch {}
    // Also drop service-worker caches and unregister workers: a stale app
    // shell is the usual reason "Loading chunk … failed" survives a reload.
    await hardResetClientCaches();
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.recovering) {
      // A reload is in flight; keep the screen quiet instead of flashing the error card.
      return null;
    }
    if (this.state.hasError) {
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback(this.state.error, () => this.setState({ hasError: false, error: null }))
          : this.props.fallback;
      }

      const isQuota =
        this.state.error?.name === 'QuotaExceededError' ||
        String(this.state.error?.message || '').toLowerCase().includes('quota');
      const isChunk = isChunkLoadMessage(this.state.error?.message);

      return (
        <div className="min-h-screen w-full bg-[#050807] text-slate-200 flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#0a1412] p-6 shadow-2xl shadow-black/80 text-center animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-4 text-amber-400">
              <AlertTriangle className="w-6 h-6" />
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              {isQuota ? 'Storage Limit Exceeded' : isChunk ? 'Update needed' : 'Something went wrong'}
            </h2>

            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              {isQuota
                ? 'Your browser storage is currently full on this device. Clear cached test runs and reload to continue.'
                : isChunk
                  ? 'Part of the app could not be downloaded, usually because a newer version was just released or the connection stalled. Clear cache & reload fetches the latest version.'
                  : (this.state.error?.message || 'An unexpected rendering error occurred while loading this view.')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
              <button
                type="button"
                onClick={this.handleReload}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold shadow-md transition"
              >
                <RefreshCw className="w-4 h-4" />
                Reload
              </button>

              <button
                type="button"
                onClick={this.handleClearCacheAndReload}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-sm font-medium transition"
              >
                <Trash2 className="w-4 h-4 text-slate-400" />
                Clear cache & reload
              </button>
            </div>

            <button
              type="button"
              onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-400 mt-2 focus:outline-none"
            >
              {this.state.showDetails ? (
                <>Hide technical details <ChevronUp className="w-3.5 h-3.5" /></>
              ) : (
                <>Show technical details <ChevronDown className="w-3.5 h-3.5" /></>
              )}
            </button>

            {this.state.showDetails && (
              <div className="mt-4 p-3 rounded-lg bg-black/50 border border-white/5 text-left font-mono text-[11px] text-rose-300/80 overflow-x-auto max-h-48 whitespace-pre-wrap select-all">
                {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
